import {
  rangeDays,
  type Entry,
  type ReportModel,
  type ReportNote,
  type ReportRow,
  type Track,
} from '@anytracker/core'
import type { ReactNode } from 'react'

import { DistributionChart } from '../../charts/DistributionChart.js'
import { OverlayChart } from '../../charts/OverlayChart.js'
import { TrackChart } from '../../charts/TrackChart.js'
import { trackColour } from '../../charts/colours.js'
import { formatDate, formatDay, formatTime } from '../../format/dates.js'
import styles from './Report.module.css'

/**
 * The printed document (T-504).
 *
 * Paginated **here**, into fixed A4 pages, rather than left to the browser's
 * own flow. That is what makes a footer and a page number possible at all:
 * `@page` margin boxes are not supported where this app runs, so the only way
 * to put "Page 3 of 8 · self-reported" at the foot of every sheet is to own the
 * page boundaries.
 *
 * Everything on it comes from `buildReport` in core. Nothing is calculated in
 * this file, so the document and the app cannot disagree about a number — which
 * matters more here than anywhere else, because this is the artefact someone
 * hands to another person and neither of them can check it against the app.
 */
export function ReportDocument({
  report,
  entries,
  deviceZone,
}: {
  readonly report: ReportModel
  readonly entries: readonly Entry[]
  /** Only for the time-axis labels; every value keeps its own captured zone. */
  readonly deviceZone: string
}) {
  const span = { from: report.range.fromEpochMs, to: report.range.toEpochMs }
  const comparable = report.overlay?.series.filter((s) => s.points.length > 0) ?? []

  const pages: ReactNode[] = [
    <SummaryPage key="summary" report={report} />,
    ...(report.overlay !== null && comparable.length > 0
      ? [
          <ChartPage key="overlay" heading="Tracks together">
            <OverlayChart
              tracks={comparable.map((series) => series.track)}
              entries={entries}
              span={span}
              size={report.bucketSize}
              aggregate="mean"
              deviceZone={deviceZone}
            />
          </ChartPage>,
        ]
      : []),
    ...report.tracks.map((track) => (
      <ChartPage key={track.track.id} heading={titleOf(track.track)}>
        {track.row.kind === 'noData' ? (
          <p className={styles['nothing']}>
            Nothing recorded for {track.track.name} in this range.
          </p>
        ) : (
          <>
            <TrackChart
              track={track.track}
              entries={entries}
              span={span}
              size={report.bucketSize}
              aggregate="mean"
              deviceZone={deviceZone}
            />
            <h3 className={styles['subHeading']}>When it was recorded</h3>
            <DistributionChart track={track.track} distribution={track.timeOfDay} />
          </>
        )}
      </ChartPage>
    )),
    ...appendixPages(report),
  ]

  return (
    <div className={styles['document']} data-testid="report-document">
      {pages.map((page, index) => (
        <article className={styles['page']} key={index} data-testid="report-page">
          <div className={styles['pageBody']}>{page}</div>
          <Footer report={report} page={index + 1} of={pages.length} />
        </article>
      ))}
    </div>
  )
}

function titleOf(track: Track): string {
  // The unit belongs beside the name, once, rather than repeated in every cell
  // of every table — the same reasoning as the CSV's wide headers (T-501).
  return track.unit === undefined ? track.name : `${track.name} (${track.unit})`
}

/**
 * The footer, on every sheet.
 *
 * "Self-reported" is not a disclaimer bolted on: it is an accurate description
 * of what the document contains, and it sets the right expectation for whoever
 * reads it. The generation date is here for the same reason the range is
 * printed as absolute days — a document with neither is undateable evidence.
 */
function Footer({
  report,
  page,
  of,
}: {
  readonly report: ReportModel
  readonly page: number
  readonly of: number
}) {
  return (
    <footer className={styles['footer']}>
      <span>Self-reported data, recorded by the person it describes.</span>
      <span>
        Generated {formatDate(report.generatedAt)} · anytracker {report.appVersion}
      </span>
      <span className={styles['pageNumber']}>
        Page {page} of {of}
      </span>
    </footer>
  )
}

function ChartPage({
  heading,
  children,
}: {
  readonly heading: string
  readonly children: ReactNode
}) {
  return (
    <>
      <h2 className={styles['pageHeading']}>{heading}</h2>
      {children}
    </>
  )
}

/**
 * The page a reader can stop at.
 *
 * The acceptance criterion is that this alone conveys the shape of the data, so
 * it carries the window, how much of it was actually logged, and one line per
 * track. Everything after it is detail for whoever wants it.
 */
function SummaryPage({ report }: { readonly report: ReportModel }) {
  const { summary } = report
  const days = rangeDays(report.range)

  return (
    <>
      <h1 className={styles['title']}>{report.title}</h1>
      {/*
        Absolute dates, never "last 30 days". A report headed with a relative
        phrase is meaningless six weeks after it was printed, which is exactly
        when someone reads it (T-402).
      */}
      <p className={styles['range']} data-testid="report-range">
        {formatDay(report.range.fromDay)} to {formatDay(report.range.toDay)} · {days}{' '}
        {days === 1 ? 'day' : 'days'}
      </p>

      <dl className={styles['headline']} data-testid="report-headline">
        <div>
          <dt>Entries</dt>
          <dd>{summary.entryCount}</dd>
        </div>
        <div>
          <dt>Days logged</dt>
          <dd>
            {summary.adherence.loggedDays} of {summary.adherence.totalDays}
          </dd>
        </div>
        <div>
          <dt>Longest gap</dt>
          <dd>
            {summary.adherence.longestGapDays}{' '}
            {summary.adherence.longestGapDays === 1 ? 'day' : 'days'}
          </dd>
        </div>
      </dl>

      {summary.sparsity.kind !== 'ok' && (
        <p className={styles['sparse']} data-testid="report-sparse">
          {summary.sparsity.kind === 'empty'
            ? 'Nothing was recorded in this period. The charts that follow have no data to draw.'
            : `Only ${summary.sparsity.loggedDays} of these ${summary.sparsity.totalDays} days were logged, so the charts show far less than the period they cover.`}
        </p>
      )}

      <table className={styles['summaryTable']} data-testid="report-summary">
        <thead>
          <tr>
            <th scope="col">Track</th>
            <th scope="col">Recorded</th>
            <th scope="col">Average</th>
            <th scope="col">Range</th>
            <th scope="col">Trend</th>
          </tr>
        </thead>
        <tbody>
          {summary.rows.map((row) => (
            <SummaryRow key={row.track.id} row={row} />
          ))}
        </tbody>
      </table>
    </>
  )
}

function SummaryRow({ row }: { readonly row: ReportRow }) {
  const name = (
    <th scope="row" className={styles['trackCell']}>
      <span
        className={styles['swatch']}
        style={{ background: trackColour(row.track.id) }}
      />
      {titleOf(row.track)}
    </th>
  )

  if (row.kind === 'noData') {
    return (
      <tr>
        {name}
        <td colSpan={4} className={styles['muted']}>
          Nothing recorded
        </td>
      </tr>
    )
  }

  if (row.kind === 'categorical') {
    return (
      <tr>
        {name}
        <td>{row.count}</td>
        {/*
          No average, and no empty cells implying one could exist. An unordered
          list of options has no mean, and a blank column invites the reader to
          assume the number was simply not calculated.
        */}
        <td colSpan={3} className={styles['counts']}>
          {/*
            A track with no categories to break down — a marker — says what the
            number is instead of leaving three columns blank. Same rule as the
            comment above: an empty cell reads as an omission.
          */}
          {row.counts.length === 0
            ? 'Counted, not measured'
            : row.counts
                .map((count) => `${count.label} ${String(count.count)}`)
                .join(' · ')}
        </td>
      </tr>
    )
  }

  return (
    <tr>
      {name}
      <td>{row.count}</td>
      <td>{row.meanLabel}</td>
      <td>{row.rangeLabel}</td>
      <td>{row.trendLabel}</td>
    </tr>
  )
}

/**
 * Roughly how many lines of the appendix fit on one page.
 *
 * An estimate, deliberately, and the honest note is that it is one: the browser
 * does the real layout and this code cannot measure it. Getting it wrong makes
 * a page number slightly off; the alternative — a fixed-height page with
 * `overflow: hidden` — silently truncates notes, and a report that quietly
 * drops what someone wrote is worse than one with an odd page count.
 */
const APPENDIX_LINES_PER_PAGE = 34
const CHARACTERS_PER_LINE = 95

function linesFor(note: ReportNote): number {
  return 1 + Math.ceil(note.note.length / CHARACTERS_PER_LINE)
}

function appendixPages(report: ReportModel): ReactNode[] {
  const appendix = report.appendix
  if (appendix === null) {
    return []
  }
  if (appendix.tags.length === 0 && appendix.notes.length === 0) {
    return []
  }

  const chunks: ReportNote[][] = []
  let current: ReportNote[] = []
  // The tag table eats into the first page's budget, so the first chunk is
  // smaller than the rest.
  let budget = APPENDIX_LINES_PER_PAGE - Math.min(appendix.tags.length + 3, 14)

  for (const note of appendix.notes) {
    const lines = linesFor(note)
    if (current.length > 0 && lines > budget) {
      chunks.push(current)
      current = []
      budget = APPENDIX_LINES_PER_PAGE
    }
    current.push(note)
    budget -= lines
  }
  if (current.length > 0) {
    chunks.push(current)
  }
  if (chunks.length === 0) {
    chunks.push([])
  }

  return chunks.map((notes, index) => (
    <div key={index}>
      <h2 className={styles['pageHeading']}>
        Appendix
        {chunks.length > 1 ? ` (${String(index + 1)} of ${String(chunks.length)})` : ''}
      </h2>

      {index === 0 && appendix.tags.length > 0 && (
        <>
          <h3 className={styles['subHeading']}>Tags</h3>
          <ul className={styles['tags']}>
            {appendix.tags.map((frequency) => (
              <li key={frequency.tag.id}>
                {frequency.tag.name} <span>{frequency.count}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {notes.length > 0 && (
        <>
          <h3 className={styles['subHeading']}>Notes</h3>
          <ol className={styles['notes']}>
            {notes.map((note, position) => (
              <li key={`${note.entryId}:${note.trackName ?? ''}:${String(position)}`}>
                <span className={styles['noteWhen']}>
                  {formatDate(note.at)} {formatTime(note.at)}
                  {note.trackName === undefined ? '' : ` · ${note.trackName}`}
                </span>
                <span className={styles['noteText']}>{note.note}</span>
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  ))
}
